import { useState, useCallback } from 'react';
import type { AIProviderConfig } from '@publishweaver/ai-providers';

const STORAGE_KEY = 'pw_studio_ai_settings';

export interface AiSettings {
  providerId: string;
  apiKey: string;
  model: string;
  ollamaBaseUrl: string;
}

const DEFAULT_SETTINGS: AiSettings = {
  providerId: 'stub',
  apiKey: '',
  model: '',
  ollamaBaseUrl: 'http://localhost:11434',
};

function loadSettings(): AiSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return { ...DEFAULT_SETTINGS, ...(JSON.parse(raw) as Partial<AiSettings>) };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function saveSettings(settings: AiSettings): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

export function useAiSettings() {
  const [settings, setSettingsState] = useState<AiSettings>(loadSettings);

  const updateSettings = useCallback((patch: Partial<AiSettings>) => {
    setSettingsState((prev) => {
      const next = { ...prev, ...patch };
      saveSettings(next);
      return next;
    });
  }, []);

  const toProviderConfig = useCallback((): AIProviderConfig => {
    return {
      providerId: settings.providerId,
      apiKey: settings.apiKey,
      model: settings.model || undefined,
      baseUrl: settings.providerId === 'ollama' ? settings.ollamaBaseUrl : undefined,
    };
  }, [settings]);

  return { settings, updateSettings, toProviderConfig };
}
